import api from "./api";
import { ImportacaoIAResultado } from "../types/iaImport";

interface ImportarIASubmitResponse {
  job_id: string;
  status: "processando";
}

export type EtapaIAStatus = "processando" | "concluido" | "erro";
export type EtapasIA = Partial<Record<"book" | "tabela", EtapaIAStatus>>;

interface ImportarIAStatusResponse extends ImportacaoIAResultado {
  status: "processando" | "concluido";
  etapas?: EtapasIA;
}

const POLL_INTERVAL_MS = 3000;
// Cobre folgadamente o pior caso (book + tabela, cada um com retry) sem
// deixar o usuário esperando pra sempre se algo travar do lado da IA.
const POLL_TIMEOUT_MS = 6 * 60 * 1000;

// Chave usada pra guardar o job em andamento no sessionStorage, assim um
// refresh acidental da página não faz o usuário perder o acompanhamento de
// um job que continua rodando no backend.
export const IA_IMPORT_JOB_STORAGE_KEY = "tactimobi:importar-ia:job-em-andamento";

const aguardar = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const iaImportService = {
  /**
   * Envia book e/ou tabela de preço pra extração via IA e aguarda o
   * resultado via polling. Pelo menos um dos dois é obrigatório (o backend
   * rejeita chamada vazia).
   *
   * Por quê polling em vez de segurar a request: o Render (host do backend)
   * tem um timeout de borda fixo de ~100s que NÃO é configurável, bem menor
   * que o tempo que book+tabela juntos podem levar no Gemini (20-90s cada).
   * Uma request síncrona longa virava 504 mesmo quando a extração terminava
   * bem do outro lado. Agora o POST só dispara o job e devolve na hora; o
   * resultado é buscado com GET /importar-ia-status/<job_id>/ até terminar.
   *
   * onProgress (opcional): chamado a cada tentativa de polling com o status
   * por etapa (book/tabela), útil pra exibir feedback granular na tela.
   */
  importar: async (
    book?: File,
    tabelaPreco?: File,
    onProgress?: (etapas: EtapasIA) => void
  ): Promise<ImportacaoIAResultado> => {
    const formData = new FormData();
    if (book) formData.append("book", book);
    if (tabelaPreco) formData.append("tabela_preco", tabelaPreco);

    const { data: submit } = await api.post<ImportarIASubmitResponse>(
      "/api/empreendimentos/importar-ia/",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    sessionStorage.setItem(IA_IMPORT_JOB_STORAGE_KEY, submit.job_id);

    try {
      return await iaImportService.acompanharJob(submit.job_id, onProgress);
    } finally {
      sessionStorage.removeItem(IA_IMPORT_JOB_STORAGE_KEY);
    }
  },

  /** Recupera o job_id de um job deixado em andamento (ex.: após um refresh). */
  recuperarJobEmAndamento: (): string | null => sessionStorage.getItem(IA_IMPORT_JOB_STORAGE_KEY),

  esquecerJobEmAndamento: (): void => sessionStorage.removeItem(IA_IMPORT_JOB_STORAGE_KEY),

  /** Faz o polling de um job já existente até ele terminar. */
  acompanharJob: async (
    jobId: string,
    onProgress?: (etapas: EtapasIA) => void
  ): Promise<ImportacaoIAResultado> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const { data, status } = await api.get<ImportarIAStatusResponse>(
        `/api/empreendimentos/importar-ia-status/${jobId}/`,
        { validateStatus: (s) => s === 200 || s === 202 }
      );
      if (status === 200) {
        const { status: _status, etapas: _etapas, ...resultado } = data;
        return resultado;
      }
      onProgress?.(data.etapas || {});
      await aguardar(POLL_INTERVAL_MS);
    }

    throw new Error("A importação via IA está demorando demais. Tente novamente.");
  },
};
